// @file View-as preview URL helpers and persona display helpers for the preview banner.
import { __ } from '@@/bits/intl'
import { clearSearchParam, getAndClearSearchParam, reload, setSearchParam } from '@@/bits/location'
import { LibraryType, WallAccessRole } from '@@/enums'

/**
 * One selectable audience on the wall, sent unclamped while previewing. `name` is null for
 * visitor and library_member (their words are localised on the client); `libraryType` is set
 * on library_member only. `role` is already filtered to the previewable set server side, so
 * `admin` and `none` may appear, and `creator` never does. `key` is exactly the `view_as` URL value.
 */
interface ViewAsPreviewPersona {
  key: string
  name: string | null
  libraryType: string | null
  role: WallAccessRole
}

const PERSONA_PARAM = 'view_as'

/**
 * Where the preview was started from. A closed set: the value crosses the URL, so an open
 * string would let a crafted link put arbitrary text into the analytics stream.
 */
type ViewAsPreviewSource = 'share_panel' | 'collaborator' | 'banner'

const SOURCE_PARAM = 'view_as_from'

function enterPreview(personaKey: string, source: ViewAsPreviewSource): void {
  setSearchParam(PERSONA_PARAM, personaKey, true)
  setSearchParam(SOURCE_PARAM, source, true)
  reload()
}

/**
 * Reads the source param and strips it in the same call, so a later manual reload reports
 * `'url'` instead of repeating the affordance that started the preview — that is what lets the
 * dashboard filter reloads out of the start count. `true` means replaceState, as in
 * `exitPreview`.
 */
function takePreviewSource(): ViewAsPreviewSource | 'url' {
  const source = getAndClearSearchParam(SOURCE_PARAM, true)
  return source === 'share_panel' || source === 'collaborator' || source === 'banner' ? source : 'url'
}

function exitPreview(): void {
  // `true` means replaceState here, so Back cannot restore the preview URL.
  clearSearchParam(PERSONA_PARAM, true)
  reload()
}

/**
 * Localised audience word for the visitor and library_member personas. Keyed on
 * `libraryType` (not the surface store) so the no-access page can reuse it without a
 * surface store. Named personas use `persona.name` instead.
 */
function audienceLabelForPersona(persona: Pick<ViewAsPreviewPersona, 'key' | 'name' | 'libraryType'>): string {
  if (persona.key === 'visitor') return __('Visitor')
  if (persona.key === 'library_member') {
    if (persona.libraryType === LibraryType.School) return __('School member')
    if (persona.libraryType === LibraryType.Classroom) return __('Classroom member')
    return __('Team member')
  }
  return persona.name ?? ''
}

function isNamedPersona(persona: Pick<ViewAsPreviewPersona, 'key'>): boolean {
  return persona.key !== 'visitor' && persona.key !== 'library_member'
}

/**
 * Splits a view-as preview banner translation into plain prefix/suffix and a bold trigger.
 * `<b>…</b>` marks the clickable phrase inside one whole-sentence translation.
 * Spaces adjoining the tags are trimmed — the banner flex row owns the gap via CSS `gap`,
 * because trailing/leading whitespace at a flex item's edge collapses in the layout box.
 */
function splitBannerCopy(translated: string): { prefix: string; trigger: string; suffix: string } {
  const match = /^(.*?)<b>(.*?)<\/b>(.*)$/.exec(translated)
  // CAT tools sometimes strip markup; keep the banner visible as one trigger.
  if (match == null) return { prefix: '', trigger: translated, suffix: '' }
  return {
    prefix: match[1].replace(/ $/, ''),
    trigger: match[2],
    suffix: match[3].replace(/^ /, ''),
  }
}

export { audienceLabelForPersona, enterPreview, exitPreview, isNamedPersona, splitBannerCopy, takePreviewSource }
export type { ViewAsPreviewPersona, ViewAsPreviewSource }
